import {
  Box,
  Button,
  Collapse,
  Dialog,
  DialogContent,
  DialogTitle,
  Step,
  StepLabel,
  Stepper,
  TextField,
} from '@mui/material';
import React, { useMemo, useState } from 'react';
import { CustomSlide } from '../../../../generic/CustomSlide';
import { IInventoryOption } from '../../../../Interfaces/IInventoryOption';
import { ILegalClient } from '../../../../Interfaces/ILegalClient';
import { ILegalTransactionRelationship } from '../../../../Interfaces/ILegalTransactionRelationship/ILegalTransactionRelationship';
import {
  ILegalTransactionRelationshipRealEstate,
  initRealEstate,
} from '../../../../Interfaces/ILegalTransactionRelationship/ILegalTransactionRelationshipRealEstate/ILegalTransactionRelationshipRealEstate';
import { IPropertyType } from '../../../../Interfaces/IPropertyType';
import { IPurchaseContractRelationType } from '../../../../Interfaces/IPurchaseContractRelationType';
import { IRealEstateUseType } from '../../../../Interfaces/IRealEstateUseType';
import {
  getNextStep,
  getPreviousStep,
  updateNames,
  validateAddress,
} from '../../utilities';
import { RealEstateInventoryOptions } from '../InventoryOption/RealEstateInventoryOptions';
import { RealEstateAddress } from '../RealEstateAddress';
import { RealEstateLandRegister } from '../RealEstateLandRegister';
import { RealEstateManager } from '../RealEstateManager';
import { RealEstatePriceUseType } from '../RealEstatePriceUseType';
import { RealEstatePropertyTypes } from '../RealEstatePropertyTypes';
import { CreateRealEstatePartialArea } from './CreateRealEstatePartialArea';
interface IProps {
  relationship: ILegalTransactionRelationship;
  propertyTypes: IPropertyType[];
  inventoryOptions: IInventoryOption[];
  realEstateUseTypes: IRealEstateUseType[];
  purchaseContractRelationTypes: IPurchaseContractRelationType[];
  setRelationship: (newValue: ILegalTransactionRelationship) => void;
  takenLegalClients: ILegalClient[];
  setTakenLegalClients: React.Dispatch<React.SetStateAction<ILegalClient[]>>;
  unmount: () => void;
}

export const CreateRealEstateDialog: React.FC<IProps> = ({
  relationship,
  propertyTypes,
  inventoryOptions,
  realEstateUseTypes,
  purchaseContractRelationTypes,
  setRelationship,
  takenLegalClients,
  setTakenLegalClients,
  unmount,
}) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [lastStep, setLastStep] = useState(0);

  const [formValues, setFormValues] =
    useState<ILegalTransactionRelationshipRealEstate>(initRealEstate());
  const [manager, setManager] = useState<ILegalClient>();

  const [isBuildSelectionMade, setIsBuildSelectionMade] = useState(false);
  const [isApartmentSelectionMade, setIsApartmentSelectionMade] =
    useState(false);
  const [isWarningPrice, setIsWarningPrice] = useState(false);

  const handleNext = () => {
    const nextStep = getNextStep(currentStep, formValues.PropertyTypeArray);
    if (nextStep !== currentStep) {
      setLastStep(currentStep);
      setCurrentStep(nextStep);
    }
  };

  const handleBack = () => {
    const prevStep = getPreviousStep(currentStep, formValues.PropertyTypeArray);
    if (prevStep !== currentStep) {
      setLastStep(currentStep);
      setCurrentStep(prevStep);
    }
  };

  const hasBuildPropertyType = useMemo(
    () => formValues.PropertyTypeArray.some(x => x.idPropertyType === 1),
    [formValues.PropertyTypeArray]
  );

  const isAddressValid = useMemo(
    () => validateAddress(formValues),
    [
      formValues.Street,
      formValues.StreetNr,
      formValues.idPostcode,
      formValues.Latitude,
      formValues.Longitude,
    ]
  );

  const onSubmit = () => {
    setRelationship({
      ...relationship,
      RealEstateArray: [...relationship.RealEstateArray, formValues],
      Members: [
        ...relationship.Members,
        ...(manager
          ? [
              {
                idRelationship: relationship.idRelationship,
                idRelationshipRole: 3,
                idLegalClient: manager.idLegalClient,
                FirstName: manager.FirstName || '',
                LastName: manager.LastName,
                LegalClient: manager,
                LegalTransactionArray: [],
                isActiveLegalClientRelationship: true,
                idLegalClientRelationship: -1,
              },
            ]
          : []),
      ],
    });

    unmount();
  };

  return (
    <Dialog open={true} onClose={unmount} maxWidth='lg' fullWidth>
      <DialogTitle>Objekt hinzufügen</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex' }}>
          <Box sx={{ width: '20%' }}>
            <Stepper activeStep={currentStep} orientation='vertical'>
              <Step key={1} completed={currentStep > 0}>
                <StepLabel>Anschrift</StepLabel>
              </Step>
              <Step key={2} completed={currentStep > 1}>
                <StepLabel>Wohn- oder Teileigentum</StepLabel>
              </Step>
              <Step key={3} completed={currentStep > 2}>
                <StepLabel>Grundbuch</StepLabel>
              </Step>
              <Step key={4} completed={currentStep > 3}>
                <StepLabel>Grundstücksform</StepLabel>
              </Step>
              <Step key={5} completed={currentStep > 4}>
                <StepLabel>Bauten</StepLabel>
              </Step>
              <Step key={6} completed={currentStep > 5}>
                <StepLabel>Teilfläche</StepLabel>
              </Step>
              <Step key={7} completed={currentStep > 6}>
                <StepLabel>Anlagen</StepLabel>
              </Step>
              <Step key={8} completed={currentStep > 7}>
                <StepLabel>Nutzung</StepLabel>
              </Step>
            </Stepper>
          </Box>
          <Box sx={{ width: '80%' }}>
            <CustomSlide
              currentPage={currentStep}
              pageNumber={0}
              lastCurrentPage={lastStep}
              appear={false}
            >
              <Box>
                <RealEstateAddress
                  formValues={formValues}
                  setFormValues={setFormValues}
                />
                <Box sx={{ mt: 5 }}>
                  <Button variant='outlined' onClick={unmount}>
                    Abrruch
                  </Button>
                  <Button
                    onClick={handleNext}
                    variant='contained'
                    sx={{ float: 'right' }}
                    disabled={!isAddressValid}
                  >
                    Weiter
                  </Button>
                </Box>
              </Box>
            </CustomSlide>
            <CustomSlide
              currentPage={currentStep}
              pageNumber={1}
              lastCurrentPage={lastStep}
            >
              <Box>
                <Box
                  display='flex'
                  height={80}
                  alignItems='center'
                  justifyContent='center'
                >
                  Liegt Wohn- oder Teileigentum vor?
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'row', pt: 2 }}>
                  <Button
                    sx={{ mr: 1 }}
                    variant={
                      !isApartmentSelectionMade || formValues.hasApartment
                        ? 'outlined'
                        : 'contained'
                    }
                    onClick={() => {
                      setIsApartmentSelectionMade(true);
                      setFormValues(x => ({ ...x, hasApartment: false }));
                    }}
                  >
                    Nein
                  </Button>
                  <Box sx={{ flex: '1 1 auto' }} />
                  <Button
                    variant={
                      !isApartmentSelectionMade || !formValues.hasApartment
                        ? 'outlined'
                        : 'contained'
                    }
                    onClick={() => {
                      setIsApartmentSelectionMade(true);
                      setFormValues(x => ({ ...x, hasApartment: true }));
                    }}
                  >
                    Ja
                  </Button>
                </Box>
                <Collapse
                  in={isApartmentSelectionMade && !!formValues.hasApartment}
                >
                  <RealEstateManager
                    takenLegalClients={takenLegalClients}
                    setTakenLegalClients={setTakenLegalClients}
                    manager={manager ? [manager] : []}
                    setManager={newValue => {
                      setRelationship({
                        ...relationship,
                        Members: updateNames(relationship.Members, newValue),
                      });
                      const newManager =
                        newValue.length > 0 ? newValue[0] : undefined;

                      setManager(newManager);
                      setFormValues(x => ({
                        ...x,
                        BuildingManager_idLegalClient: newManager
                          ? newManager.idLegalClient
                          : null,
                      }));
                    }}
                  />
                </Collapse>
                <Box sx={{ mt: 5 }}>
                  <Button variant='outlined' onClick={handleBack}>
                    Zurück
                  </Button>
                  <Button
                    onClick={handleNext}
                    variant='contained'
                    sx={{ float: 'right' }}
                    disabled={
                      !isApartmentSelectionMade ||
                      (formValues.hasApartment && !manager)
                    }
                  >
                    Weiter
                  </Button>
                </Box>
              </Box>
            </CustomSlide>
            <CustomSlide
              currentPage={currentStep}
              pageNumber={2}
              lastCurrentPage={lastStep}
            >
              <Box>
                <RealEstateLandRegister
                  formValues={formValues}
                  setFormValues={setFormValues}
                />
                <Box sx={{ mt: 5 }}>
                  <Button variant='outlined' onClick={handleBack}>
                    Zurück
                  </Button>
                  <Button
                    onClick={handleNext}
                    variant='contained'
                    sx={{ float: 'right' }}
                  >
                    Weiter
                  </Button>
                </Box>
              </Box>
            </CustomSlide>
            <CustomSlide
              currentPage={currentStep}
              pageNumber={3}
              lastCurrentPage={lastStep}
            >
              <Box>
                <RealEstatePropertyTypes
                  formValues={formValues}
                  setFormValues={setFormValues}
                  propertyTypes={propertyTypes}
                />
                <Box sx={{ mt: 5 }}>
                  <Button variant='outlined' onClick={handleBack}>
                    Zurück
                  </Button>
                  <Button
                    onClick={handleNext}
                    disabled={formValues.PropertyTypeArray.length === 0}
                    variant='contained'
                    sx={{ float: 'right' }}
                  >
                    Weiter
                  </Button>
                </Box>
              </Box>
            </CustomSlide>
            <CustomSlide
              currentPage={currentStep}
              pageNumber={4}
              lastCurrentPage={lastStep}
            >
              <Box>
                <Collapse appear={false} in={!hasBuildPropertyType}>
                  <Box
                    display='flex'
                    height={80}
                    alignItems='center'
                    justifyContent='center'
                  >
                    Ist das Grundstück (oder ein Teil davon) bebaut?
                  </Box>
                  <Box sx={{ display: 'flex', flexDirection: 'row', pt: 2 }}>
                    <Button
                      sx={{ mr: 1 }}
                      variant={
                        !isBuildSelectionMade || formValues.isBuild
                          ? 'outlined'
                          : 'contained'
                      }
                      onClick={() => {
                        setIsBuildSelectionMade(true);
                        setFormValues(x => ({
                          ...x,
                          isBuild: false,
                          BuildWith: null,
                        }));
                      }}
                    >
                      Unbebaut
                    </Button>
                    <Box sx={{ flex: '1 1 auto' }} />
                    <Button
                      variant={
                        !isBuildSelectionMade || !formValues.isBuild
                          ? 'outlined'
                          : 'contained'
                      }
                      onClick={() => {
                        setIsBuildSelectionMade(true);
                        setFormValues(x => ({ ...x, isBuild: true }));
                      }}
                    >
                      Bebaut
                    </Button>
                  </Box>
                </Collapse>
                <Collapse
                  in={
                    (isBuildSelectionMade && !!formValues.isBuild) ||
                    hasBuildPropertyType
                  }
                >
                  <TextField
                    sx={{ mt: 3 }}
                    label='Bauten'
                    size='small'
                    fullWidth
                    value={formValues.BuildWith ?? ''}
                    onChange={({
                      target,
                    }: React.ChangeEvent<HTMLInputElement>) =>
                      setFormValues(x => ({ ...x, BuildWith: target.value }))
                    }
                    multiline
                    rows={3}
                  />
                </Collapse>
                <Box sx={{ mt: 5 }}>
                  <Button variant='outlined' onClick={handleBack}>
                    Zurück
                  </Button>
                  <Button
                    onClick={handleNext}
                    variant='contained'
                    sx={{ float: 'right' }}
                    disabled={!isBuildSelectionMade && !hasBuildPropertyType}
                  >
                    Weiter
                  </Button>
                </Box>
              </Box>
            </CustomSlide>
            <CustomSlide
              currentPage={currentStep}
              pageNumber={5}
              lastCurrentPage={lastStep}
            >
              <CreateRealEstatePartialArea
                handleBack={handleBack}
                handleNext={handleNext}
                formValues={formValues}
                setFormValues={setFormValues}
                purchaseContractRelationTypes={purchaseContractRelationTypes}
              />
            </CustomSlide>
            <CustomSlide
              currentPage={currentStep}
              pageNumber={6}
              lastCurrentPage={lastStep}
            >
              <Box>
                <RealEstateInventoryOptions
                  formValues={formValues}
                  setFormValues={setFormValues}
                  inventoryOptions={inventoryOptions}
                />
                <Box sx={{ mt: 5 }}>
                  <Button variant='outlined' onClick={handleBack}>
                    Zurück
                  </Button>
                  <Button
                    onClick={handleNext}
                    disabled={formValues.PropertyTypeArray.length === 0}
                    variant='contained'
                    sx={{ float: 'right' }}
                  >
                    Weiter
                  </Button>
                </Box>
              </Box>
            </CustomSlide>
            <CustomSlide
              currentPage={currentStep}
              pageNumber={7}
              lastCurrentPage={lastStep}
            >
              <Box>
                <RealEstatePriceUseType
                  formValues={formValues}
                  setFormValues={setFormValues}
                  realEstateUseTypes={realEstateUseTypes}
                  isWarningPrice={isWarningPrice}
                  setIsWarningPrice={setIsWarningPrice}
                />
                <Box sx={{ mt: 5 }}>
                  <Button variant='outlined' onClick={handleBack}>
                    Zurück
                  </Button>
                  <Button
                    onClick={onSubmit}
                    disabled={isWarningPrice}
                    variant='contained'
                    sx={{ float: 'right' }}
                  >
                    Hinzufügen
                  </Button>
                </Box>
              </Box>
            </CustomSlide>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
};
